<template>
  <div>
    <BCard body-class="gap-[32px] d-flex flex-column">
      <!-- Header Section -->
      <div class="text-black text-2xl font-semibold d-flex">
        <button
          class="button-primary text-white flex items-center justify-center mr-1 px-[5px] w-10 h-10"
          @click="$router.replace('/organization')"
        >
          <span class="k-arrow-left-2 h-100 w-100 text-5 flex items-center justify-center" />
        </button>
        <span class="text-10 text--dark">Detail Organisasi</span>
      </div>

      <!-- Info Card -->
      <div class="flex flex-col lg:flex-row justify-between items-center gap-3 bg-[#f5f5f5] shadow-md rounded-lg px-2 py-1">
        <span class="text-black font-semibold break-all">{{ detailOrganization.name }}</span>
        <div class="flex flex-col md:flex-row gap-[20px]">
          <div class="flex flex-col md:flex-row">
            <span class="text-base text-[#737373] mr-1">Tanggal Dibuat:</span>
            <span class="text-base text-black font-semibold">{{ DAY_MONTH_YEAR_COMMA_TIME(detailOrganization.created_at) }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="text-base text-[#737373] mr-1">Akun Terhubung:</span>
            <span class="text-base text-black font-semibold">{{ detailOrganization.total_account }}</span>
          </div>
        </div>
      </div>

      <!-- Action Button -->
      <div class="flex flex-col md:flex-row items-center justify-end gap-2">
        <div class="w-full md:w-auto">
          <BButton
            variant="light"
            size="md"
            class="!flex items-center justify-center gap-2 !px-6 !py-3 !shadow !bg-white !border-[#E5E5E5] !rounded-lg"
            @click="$bvModal.show('modal-link-account')"
          >
            <img
              src="https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/add.svg"
              alt="filter"
              width="16"
              height="16"
            >
            <span>Hubungkan Akun</span>
          </BButton>
        </div>
      </div>

      <!-- Table Section -->
      <BOverlay
        variant="light"
        :show="isLoading"
        spinner-variant="primary"
        blur="0"
        opacity=".5"
        rounded="sm"
      >
        <div class="border rounded-lg overflow-hidden">
          <BTable
            :fields="fields"
            :items="items"
            responsive
            show-empty
            thead-class="bg-[#F9FAFB]"
            tbody-tr-class="hover:bg-slate-100 transition-colors duration-300 cursor-pointer"
          >
            <template #cell(no)="data">
              <div class="text-base max-w-[100px]">
                {{ (currentPage - 1) * totalPerPage + data.index + 1 }}
              </div>
            </template>

            <template #cell(email)="data">
              <div class="truncate max-w-[250px]">
                {{ data.item.email }}
              </div>
            </template>

            <template #cell(level)="data">
              <div
                v-if="data.item.user_level && data.item.user_level.length"
                class="max-w-[250px] flex gap-1 items-center"
              >
                <template v-for="(icon, index) in data.item.user_level">
                  <img
                    v-if="icon.icons"
                    :key="`icon-${index}`"
                    v-b-tooltip.hover.top="formatUserLevelTooltip(icon)"
                    :src="icon.icons"
                    alt="level"
                    width="20"
                    class="mr-[5px]"
                  >
                  <span
                    v-else
                    :key="`dash-${index}`"
                  >
                    -
                  </span>
                </template>
              </div>
              <div
                v-else
                class="truncate max-w-[250px]"
              >
                -
              </div>
            </template>

            <template #cell(product)="data">
              <div
                v-if="data.item.product_icons && data.item.product_icons.length"
                class="max-w-[250px] flex gap-1 items-center flex-wrap"
              >
                <img
                  v-for="(icon, idx) in data.item.product_icons"
                  :key="idx"
                  :src="icon"
                  alt="product"
                  :class="icon && icon.toLowerCase().includes('rajaongkir') ? 'w-[16px] h-[16px]' : 'w-[20px] h-[20px]'"
                >
              </div>
              <div
                v-else
                class="truncate max-w-[250px]"
              >
                -
              </div>
            </template>

            <template #cell(komcards_score)="data">
              <div class="truncate max-w-[250px]">
                {{ getKomcardScore(data.item) }}
              </div>
            </template>

            <template #cell(action)="data">
              <div
                v-if="!data.item.is_main_account"
                class="flex justify-center items-center"
              >
                <BDropdown
                  variant="none"
                  no-caret
                  right
                  toggle-class="p-0 bg-transparent border-0 flex items-center justify-center"
                  size="sm"
                >
                  <template #button-content>
                    <img
                      src="https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/more.svg"
                      alt="more-icon"
                      width="20"
                      height="20"
                      class="shrink-0 object-contain"
                    >
                  </template>
                  <div class="flex flex-col gap-[5px] p-[5px] w-[200px]">
                    <div
                      class="flex gap-[3px] items-center px-[4px] hover:bg-[#f8f8f8] text-[#626262] text-[14px] py-[5px] cursor-pointer"
                      @click="handleActionSetMain(data.item)"
                    >
                      <div>Jadikan Main</div>
                    </div>
                    <div
                      class="flex gap-[3px] items-center px-[4px] hover:bg-[#f8f8f8] text-[#DC2626] text-[14px] py-[5px] cursor-pointer"
                      @click="handleActionUnlink(data.item)"
                    >
                      <div>Unlink Account</div>
                    </div>
                  </div>
                </BDropdown>
              </div>
              <div
                v-else
                class="flex justify-center items-center"
              >
                <BDropdown
                  variant="none"
                  no-caret
                  right
                  toggle-class="p-0 bg-transparent border-0 flex items-center justify-center"
                  size="sm"
                >
                  <template #button-content>
                    <span class="flex items-center justify-center bg-[#F95031] text-white rounded-full px-2 py-1 text-sm font-medium h-[36px]">
                      Main Account
                    </span>
                  </template>
                  <div class="flex flex-col gap-[5px] p-[5px] w-[200px]">
                    <div
                      class="flex gap-[3px] items-center px-[4px] hover:bg-[#f8f8f8] text-[#DC2626] text-[14px] py-[5px] cursor-pointer"
                      @click="handleRemoveAccount(data.item)"
                    >
                      <div>Remove Main Account</div>
                    </div>
                  </div>
                </BDropdown>
              </div>
            </template>

            <template #empty>
              <div class="flex flex-col items-center justify-center py-[120px]">
                <p class="text-[#6B7280] text-[16px] mb-1">
                  Tidak ada akun tertaut
                </p>
                <p class="text-[#9CA3AF] text-[14px] lg:w-[400px] w-[250px] text-wrap text-center">
                  Tautkan akun partner untuk mengelompokkan partner sesuai dengan benefit yang dia dapatkan
                </p>
                <BButton
                  variant="primary"
                  class="!bg-[#F95031] !border-[#F95031] !rounded-lg !px-5 !py-2"
                  @click="$bvModal.show('modal-link-account')"
                >
                  Hubungkan Akun
                </BButton>
              </div>
            </template>
          </BTable>
        </div>
      </BOverlay>

      <!-- Pagination Section -->
      <BCardBody class="d-flex justify-content-between flex-wrap pt-0 mt-1">
        <BFormGroup
          label="Per Page"
          label-cols="6"
          label-align="left"
          label-size="sm"
          label-for="perPageSelect"
          class="text-nowrap mb-md-0 mr-1"
        >
          <BFormSelect
            id="perPageSelect"
            v-model="totalPerPage"
            size="sm"
            inline
            :options="optionsPage"
          />
        </BFormGroup>

        <div>
          <BPagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="totalPerPage"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
            class="mb-0"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </BPagination>
        </div>
      </BCardBody>
    </BCard>

    <ModalLink @submit="getListAccount" />

    <ModalConfirm
      id="modal-remove-main-account"
      title="Remove Main Account"
      description="Seluruh benefit dari Main Account akan hilang. Kamu yakin ingin menghentikan akun ini sebagai Main Account?"
      confirm-text="Remove"
      cancel-text="Batal"
      type-modal="remove"
      @confirm="handleConfirmRemoveMainAccount"
    />

    <ModalConfirm
      id="modal-set-main-account"
      title="Set Akun sebagai Main Account"
      description="Apakah kamu yakin ingin set akun ini sebagai Main Account?"
      confirm-text="Konfirmasi"
      cancel-text="Batal"
      type-modal="set-main"
      @confirm="handleConfirmSetMainAccount"
    />

    <ModalConfirm
      id="modal-unlink-account"
      title="Lepaskan akun"
      description="Apakah kamu yakin ingin melepas akun dari organisasi ini?"
      confirm-text="Hapus"
      cancel-text="Batal"
      type-modal="unlink"
      @confirm="handleConfirmUnlinkAccount"
    >
      <template #body>
        <div
          v-if="selectedAccount"
          class="border border-[#E5E5E5] rounded-lg p-2 mx-3 mb-2 flex flex-col justify-center"
        >
          <div class="text-[#333333] font-semibold text-[14px]">
            {{ selectedAccount.email }}
          </div>
          <div
            v-if="selectedAccount.product_icons && selectedAccount.product_icons.length"
            class="max-w-[250px] flex gap-1 items-center mt-1"
          >
            <img
              v-for="(lvl, idx) in selectedAccount.product_icons"
              :key="idx"
              :src="lvl"
              alt="level"
              width="20"
            >
          </div>
          <div
            v-else
            class="mt-1"
          >
            -
          </div>
        </div>
        <p class="text-[#DC2626] text-[14px] px-3 mb-3">
          Akun akan kehilangan akses semua benefit Organisasi yang diwariskan
        </p>
      </template>
    </ModalConfirm>
  </div>
</template>

<script>
import { newAxiosIns } from '@/libs/axios'
import { DAY_MONTH_YEAR_COMMA_TIME } from '@/libs/filterDate'
import ModalConfirm from './ModalConfirm.vue'
import ModalLink from './ModalLink.vue'
import { fieldDetailOrganization } from './config'

export default {
  components: {
    ModalLink,
    ModalConfirm,
  },
  data() {
    return {
      fields: fieldDetailOrganization,
      items: [],
      detailOrganization: null,
      selectedAccount: null,
      DAY_MONTH_YEAR_COMMA_TIME,

      // State
      isLoading: false,
      isSubmitting: false,

      // Pagination
      currentPage: 1,
      totalPerPage: 20,
      totalRows: 0,
      optionsPage: [20, 50, 75],
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalRows / this.totalPerPage))
    },
  },
  watch: {
    currentPage() {
      this.getListAccount()
    },
    totalPerPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (this.currentPage === 1) {
          this.getListAccount()
        } else {
          this.currentPage = 1
        }
      }
    },
  },
  mounted() {
    this.getListAccount()
  },
  methods: {
    async getDetailOrganization() {
      try {
        const url = '/auth/api/v1/organizations/info'
        const params = {
          id_org: this.$route.params.id,
        }
        const { data } = await newAxiosIns.get(url, { params })
        this.detailOrganization = data?.data || null
      } catch {
        this.$toast_error({ message: 'Gagal load data' })
      }
    },

    async getListAccount() {
      try {
        this.isLoading = true
        const url = '/auth/api/v1/organizations/accounts'
        const params = {
          page: this.currentPage,
          per_page: this.totalPerPage,
          id_org: this.$route.params.id,
        }

        const { data } = await newAxiosIns.get(url, { params })
        this.items = data?.data || []
        this.totalRows = data?.meta?.pagination?.total || 0
        this.getDetailOrganization()
      } catch {
        this.$toast_error({ message: 'Gagal load data' })
      } finally {
        this.isLoading = false
      }
    },

    async handleConfirmRemoveMainAccount() {
      try {
        this.isSubmitting = true
        const url = '/auth/api/v1/organizations/accounts/set-main'
        const params = {
          id_org: parseInt(this.$route.params.id, 10),
          user_id: this.selectedAccount.user_id,
        }

        const { data } = await newAxiosIns.delete(url, { data: params })
        this.$bvModal.hide('modal-remove-main-account')
        this.$toast_success({ message: 'Kamu berhasil melepaskan main akun email partner' })
        this.getListAccount()
      } catch (error) {
        this.$toast_error({ message: error?.response?.data?.message || error?.message || 'Terjadi kesalahan' })
      } finally {
        this.isSubmitting = false
      }
    },

    handleActionSetMain(item) {
      const hasKomcards = Array.isArray(item?.product_icons) && item.product_icons.some(icon => typeof icon === 'string' && icon.toLowerCase().includes('komcards'))
      if (!hasKomcards) {
        this.$toast_error({ message: 'Akun tidak terdaftar di Komcards' })
        return
      }
      this.selectedAccount = item
      this.$bvModal.show('modal-set-main-account')
    },

    handleRemoveAccount(item) {
      this.selectedAccount = item
      this.$bvModal.show('modal-remove-main-account')
    },

    async handleConfirmSetMainAccount() {
      try {
        this.isSubmitting = true
        const url = '/auth/api/v1/organizations/accounts/set-main'
        const params = {
          id_org: parseInt(this.$route.params.id, 10),
          user_id: this.selectedAccount.user_id,
        }

        const { data } = await newAxiosIns.post(url, params)
        this.$toast_success({ message: data?.message || 'Berhasil menjadikan email partner sebagai main akun' })
        this.$bvModal.hide('modal-set-main-account')
        this.getListAccount()
      } catch (error) {
        this.$toast_error({ message: error?.response?.data?.message || error?.message || 'Terjadi kesalahan' })
      } finally {
        this.isSubmitting = false
      }
    },

    handleActionUnlink(item) {
      this.selectedAccount = item
      this.$bvModal.show('modal-unlink-account')
    },

    async handleConfirmUnlinkAccount() {
      try {
        this.isSubmitting = true
        const url = '/auth/api/v1/organizations/link-account'
        const params = {
          id_org: parseInt(this.$route.params.id, 10),
        }

        const product = this.selectedAccount.source || 'komerce'
        if (product === 'komerce') {
          if (this.selectedAccount.user_id) params.user_ids = [JSON.stringify(this.selectedAccount.user_id)]
        } else {
          params.external_users = [
            {
              email: this.selectedAccount.email || '',
              external_id: String(this.selectedAccount.external_id || this.selectedAccount.user_id || this.selectedAccount.id || ''),
              product,
            },
          ]
        }

        const { data } = await newAxiosIns.delete(url, { data: params })
        this.$toast_success({ message: data?.message || 'Berhasil melepaskan akun email partner' })
        this.$bvModal.hide('modal-unlink-account')
        this.getListAccount()
      } catch (error) {
        this.$toast_error({ message: error?.response?.data?.message || error?.message || 'Terjadi kesalahan' })
      } finally {
        this.isSubmitting = false
      }
    },

    getKomcardScore(item) {
      if (!item || !Array.isArray(item.product_icons) || !item.product_icons.length) return '-'
      const hasKomcards = item.product_icons.some(icon => typeof icon === 'string' && icon.toLowerCase().includes('komcards'))
      if (!hasKomcards) return '-'
      return item.komcard_score || 0
    },

    formatUserLevelTooltip(icon) {
      if (!icon) return ''
      const capitalize = str => (str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '')
      const level = capitalize(icon.level)
      const product = capitalize(icon.product)
      return `${level} ${product}`.trim()
    },
  },
}
</script>
